import { observer } from "mobx-react-lite";

import { ParameterInput } from "../ParameterInput";
import { ParameterMultiSelect } from "../ParameterMultiSelect";
import { Select } from "../ParameterSelect";
import { DropdownValue } from "../ParameterSelect/types";
import { ParameterTags } from "../ParameterTags";

import { ParameterControlContentProps } from "./types";

import { ComponentType } from "@/queries/models/types";
import { Slider } from "@/components/Slider";
import { Input } from "@/components/ui/input";
import { ParameterValue } from "@/components/ModelParameters/utils";

export const ParameterControlContent = observer(
  ({
    parameter,
    value,
    onChange,
    isDisabled,
    readOnly,
    isPreview,
  }: ParameterControlContentProps) => {
    switch (parameter.componentType) {
      case ComponentType.SLIDER:
        if (parameter.min === parameter.max) {
          return <Input value={String(parameter.min)} disabled readOnly className="h-9 w-full" />;
        }
        return (
          <Slider
            value={value as string}
            defaultValue={String(parameter.default)}
            onValueChange={onChange}
            min={parameter.min as number}
            hasInput
            max={parameter.max as number}
            step={parameter.step as number}
            isDisabled={isDisabled}
            readOnly={readOnly}
            isPreview={isPreview}
          />
        );
      case ComponentType.INPUT: {
        const { isNumericInput } = parameter;
        // Number("") === 0 — never coerce an empty string default into a fake zero.
        const numericDefault =
          parameter.default === "" || parameter.default === null || parameter.default === undefined
            ? undefined
            : Number(parameter.default);

        return (
          <ParameterInput
            value={value?.toString()}
            type={isNumericInput ? "number" : "text"}
            defaultValue={isNumericInput ? numericDefault : String(parameter.default ?? "")}
            onChange={onChange}
            isDisabled={isDisabled}
            placeholder={parameter.placeholder || ""}
            readOnly={readOnly}
            isPreview={isPreview}
          />
        );
      }
      case ComponentType.DROPDOWN:
        return (
          <Select
            isDisabled={isDisabled}
            selectedDropdownValue={value as DropdownValue}
            options={parameter.options || []}
            onChange={val => onChange(val as ParameterValue)}
            defaultValue={String(parameter.default)}
            placeholder={parameter.placeholder || ""}
            readOnly={readOnly}
            isPreview={isPreview}
          />
        );
      case ComponentType.MULTI_SELECT:
        return (
          <ParameterMultiSelect
            isDisabled={isDisabled}
            value={value as string[]}
            options={parameter.options || []}
            onChange={val => onChange(val as ParameterValue)}
            placeholder={parameter.placeholder || ""}
            readOnly={readOnly}
            isPreview={isPreview}
          />
        );
      case ComponentType.TAGS:
        return (
          <ParameterTags
            value={value as string[] | string}
            onChange={val => onChange(val as ParameterValue)}
            defaultValue={parameter.default as string[] | string}
            isDisabled={isDisabled}
            placeholder={parameter.placeholder || ""}
            readOnly={readOnly}
            isPreview={isPreview}
          />
        );
      default:
        return null;
    }
  },
);
